import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import Avatar from '@shared/ui/Avatar';
import { BrandContact } from './BrandContact';
import { currentLocale } from '@shared/i18n';
import { useDialogFocus } from '@shared/hooks/useDialogFocus';

interface BrandRow {
  brand_id: string; name: string; handle: string | null; avatar: string | null;
  posts: number; last_at: string | null; can_message: boolean;
}
interface History { ok: boolean; total?: number; brands?: BrandRow[]; reason?: string }
interface PostRow { id: string; permalink: string | null; thumb_public_url: string | null; posted_at: string | null; likes: number | null }

const SHOW = 4;

const monthAgo = (iso: string | null): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return (Date.now() - d.getTime()) / 86_400_000 / 30.4;
};

/**
 * "Brands you've worked with" — the portfolio creators otherwise keep by hand.
 * Tapping a brand opens the posts that mentioned it. The action button adapts:
 * brands registered on AdaMar get an in-app chat, everyone else gets a share the
 * creator sends themselves (most brands aren't on the platform, so a chat-only
 * button would be dead ~95% of the time).
 */
export default function CreatorBrandHistory({ all = false, bare = false }: { all?: boolean; bare?: boolean } = {}) {
  const { t } = useTranslation('provider');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o }) as string;
  const [expanded, setExpanded] = useState(false);
  const [openBrand, setOpenBrand] = useState<BrandRow | null>(null);

  const { data, isLoading } = useSupabaseQuery<History>(
    ['my-brand-history'],
    async () => supabase.rpc('get_my_brand_history'),
  );
  const brands = data?.brands ?? [];
  if (isLoading || !data?.ok || !brands.length) return null;

  const shown = all || expanded ? brands : brands.slice(0, SHOW);



  return (
    <div style={{ marginTop: bare ? 0 : 20 }}>
      {!bare && (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '0 2px 8px' }}>
          <span style={{ fontSize: 12, color: 'var(--dim)' }}>{tr('creator.brands.title', 'С кем вы уже работали')}</span>
          <span style={{ fontSize: 11, color: 'var(--dim)' }}>{tr('creator.brands.countN', '{{count}} брендов', { count: data.total })}</span>
        </div>
      )}

      <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, overflow: 'hidden' }}>
        {shown.map((b, i) => {
          const months = monthAgo(b.last_at);
          const stale = months != null && months >= 3;
          return (
            <div key={b.brand_id} style={{ borderTop: i ? '1px solid rgba(255,255,255,0.06)' : 'none', padding: '9px 11px' }}>
              <button type="button" onClick={() => setOpenBrand(b)}
                style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                <Avatar src={b.avatar} name={b.name} size={30} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', color: 'var(--text)', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                  <span style={{ display: 'block', color: 'var(--dim)', fontSize: 11 }}>
                    {tr('creator.brands.postsN', '{{count}} публикаций', { count: b.posts })}
                    {b.last_at && ` · ${new Date(b.last_at).toLocaleDateString(currentLocale(), { month: 'short', year: '2-digit' })}`}
                  </span>
                </span>
                <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3, color: '#8fb0e6', fontSize: 12, fontWeight: 600, border: '1px solid rgba(120,150,200,0.28)', borderRadius: 8, padding: '5px 9px' }}>
                  {tr('creator.brands.open', 'Работы')} <span style={{ fontSize: 13, lineHeight: 1 }}>›</span>
                </span>
              </button>

              {stale && (
                <div style={{ color: '#e6c84a', fontSize: 11, margin: '5px 0 0 39px' }}>
                  {tr('creator.brands.stale', 'Давно не работали — напомните о себе')}
                </div>
              )}

              <div style={{ display: 'flex', gap: 6, margin: '7px 0 0 39px' }}>
                <BrandContact compact brand={{ brand_id: b.brand_id, name: b.name, handle: b.handle, can_message: b.can_message, posts: b.posts }} />
              </div>
            </div>
          );
        })}
      </div>

      {!all && brands.length > SHOW && (
        <button type="button" onClick={() => setExpanded((v) => !v)}
          style={{ marginTop: 8, background: 'transparent', border: 'none', color: '#6699ff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
          {expanded ? tr('creator.brands.less', 'Свернуть') : tr('creator.brands.all', 'Показать все {{n}}', { n: brands.length })}
        </button>
      )}

      {openBrand && <BrandPosts brand={openBrand} tr={tr} onClose={() => setOpenBrand(null)} />}
    </div>
  );
}

/** Posts that mentioned one brand — the "open a brand" view. */
function BrandPosts({ brand, tr, onClose }: { brand: BrandRow; tr: (k: string, d: string, o?: Record<string, unknown>) => string; onClose: () => void }) {
  const { data, isLoading } = useSupabaseQuery<PostRow[]>(
    ['my-posts-for-brand', brand.brand_id],
    async () => supabase.rpc('get_my_posts_for_brand', { p_brand: brand.brand_id }),
  );
  const posts = data ?? [];
  const panelRef = useDialogFocus(onClose);
  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'flex-end' }}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={brand.name} onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', outline: 'none', maxHeight: '82vh', background: '#0e1119', borderRadius: '14px 14px 0 0', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Avatar src={brand.avatar} name={brand.name} size={30} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--text)', fontWeight: 600, fontSize: 14 }}>{brand.name}</div>
            <div style={{ color: 'var(--dim)', fontSize: 11 }}>{tr('creator.brands.postsN', '{{count}} публикаций', { count: brand.posts })}</div>
          </div>
          <button type="button" onClick={onClose} aria-label={tr('creator.close', 'Закрыть')} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', color: 'var(--text)', width: 44, height: 44, borderRadius: 8, cursor: 'pointer', fontSize: 17, lineHeight: 1, fontFamily: 'inherit' }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {isLoading && <div style={{ color: 'var(--dim)', textAlign: 'center', padding: 24, fontSize: 12 }}>…</div>}
          {!isLoading && !posts.length && (
            <div style={{ color: 'var(--dim)', textAlign: 'center', padding: 24, fontSize: 12 }}>
              {tr('creator.brands.noPosts', 'Публикации не найдены.')}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
            {posts.map((p) => (
              <a key={p.id} href={p.permalink ?? '#'} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                <div style={{ position: 'relative', aspectRatio: '3/4', borderRadius: 8, overflow: 'hidden', background: '#161a24', border: '1px solid rgba(255,255,255,0.07)' }}>
                  {p.thumb_public_url && <img src={p.thumb_public_url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  {p.likes != null && (
                    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '14px 5px 4px', background: 'linear-gradient(transparent, rgba(0,0,0,0.8))', color: '#fff', fontSize: 11, fontWeight: 600 }}>
                      ♥ {p.likes.toLocaleString()}
                    </div>
                  )}
                </div>
                {p.posted_at && <div style={{ color: 'var(--dim)', fontSize: 11, marginTop: 3 }}>{new Date(p.posted_at).toLocaleDateString(currentLocale(), { day: '2-digit', month: 'short' })}</div>}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
